import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Descriptions,
  message,
  Skeleton,
  Button,
  Space,
  Form as AntForm,
} from 'antd';
import { useRole } from 'common/hooks/useRole';
import {
  BusinessPartnerForm,
  BusinessPartnerFormModel,
} from 'pages/BusinessPartnerManagement/BusinessPartnerForm';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { FormikOnSubmit } from 'lib/formik.utils';
import { QueryKey } from 'api/queryKey';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useEnsureContext } from 'common/hooks';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { BusinessPartnerIntegrationCreateButton } from 'pages/SuperAdminModule/BusinessPartnersManagement/BusinessPartnersListPage/BusinessPartnerIntegrationCreateButton';
import type { FormikProps } from 'formik';

export const BusinessPartnerPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const { isSuperAdmin } = useRole();
  const queryCache = useQueryCache();
  const intlMessage = useFormatMessage();

  const { isLoading, data } = useQuery(
    [QueryKey.BusinessPartner, bpContext.id],
    () => BusinessPartnersAPI.fetchById(bpContext.id)
  );

  const [mutate, { isLoading: isSubmitting }] = useMutation(
    BusinessPartnersAPI.update
  );

  const [isEditMode, setIsEditMode] = useState(false);
  const formikRef = useRef<FormikProps<any>>(null);

  const formValue: BusinessPartnerFormModel | undefined = useMemo(() => {
    if (!data) return undefined;
    return {
      partnerModel: data.partnerType ?? data.partnerModel ?? undefined,
      name: data.name ?? '',
      uniqueNumber: data.uniqueNumber ?? '',
      subscription: data.subscriptionConfig?.subscriptionPlan ?? undefined,
      examLimit: data.subscriptionConfig?.customExaminationsLimit ?? null,
      description: data.description ?? '',
    };
  }, [data]);

  const handleSubmit: FormikOnSubmit<any> = useCallback(
    (formData, helpers) => {
      return (mutate({ id: bpContext.id, ...formData } as any, {
        onSuccess: (updated) => {
          queryCache.invalidateQueries(QueryKey.BusinessPartnersList).then();

          const merged: any = {
            ...(data ?? {}),
            ...(updated ?? {}),
            subscriptionConfig: {
              ...(data?.subscriptionConfig ?? {}),
              ...(updated?.subscriptionConfig ?? {}),
              ...(formData?.subscriptionConfig ?? {}),
            },
          };
          queryCache.setQueryData(
            [QueryKey.BusinessPartner, bpContext.id],
            merged
          );
          queryCache
            .invalidateQueries([QueryKey.BusinessPartner, bpContext.id])
            .then();

          message.success(
            intlMessage(
              { id: 'businessPartner.updated' },
              { name: formData.name }
            )
          );
          setIsEditMode(false);
        },
        onSettled: () => {
          helpers.setSubmitting(false);
        },
      }) as unknown) as Promise<void>;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bpContext.id, queryCache, mutate, data]
  );

  const handlePrimaryButton = () => {
    if (!isEditMode) {
      setIsEditMode(true);
      return;
    }
    formikRef.current?.submitForm();
  };

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 14 },
  };
  const tailLayout = {
    wrapperCol: { offset: 8, span: 14 },
  };

  return (
    <>
      {isLoading && <Skeleton />}
      {!isLoading && (
        <>
          {isSuperAdmin && (
            <BusinessPartnerIntegrationCreateButton
              businessPartner={bpContext}
            />
          )}

          <BusinessPartnerForm
            onSubmit={handleSubmit}
            loading={isSubmitting}
            value={formValue}
            readonly={!isEditMode}
            lockIdentityFields
            hideInternalSubmit
            formikRef={formikRef}
          />

          <AntForm {...layout}>
            <AntForm.Item
              {...tailLayout}
              style={{ marginTop: 16, textAlign: 'right' }}
            >
              <Space>
                {isSuperAdmin && (
                  <Button
                    id={
                      isEditMode
                        ? 'button-save-business-partner'
                        : 'button-change-subscription-plan'
                    }
                    type="primary"
                    onClick={handlePrimaryButton}
                    loading={isSubmitting}
                  >
                    {isEditMode
                      ? intlMessage({ id: 'save' })
                      : intlMessage({ id: 'businessPartners.editSettings' })}
                  </Button>
                )}

                {isEditMode && (
                  <Button
                    id="button-cancel-edit-business-partner"
                    onClick={() => {
                      setIsEditMode(false);
                      if (formValue) {
                        formikRef.current?.resetForm({
                          values: formValue as any,
                        });
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
              </Space>
            </AntForm.Item>
          </AntForm>

          <Descriptions style={{ marginTop: 24 }}>
            <Descriptions.Item label={intlMessage({ id: 'version' })}>
              {process.env.VERSION}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </>
  );
};
